// src/firebase/comments.js
// Comment Bank Service - manages reusable feedback comments

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { getCurrentUserOrgId } from './orgs';
import { getHomeworkNames, sortHomeworkTypes } from './settings';

// Each homework type's comment bank lives at orgs/{orgId}/commentBank/{homeworkType}.
// Every function below accepts an optional trailing orgId that overrides the
// auto-resolved "current signed-in user's org" - see settings.js for the
// same pattern and when to pass it explicitly.
const getCommentBankRef = async (homeworkType, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  return doc(db, 'orgs', resolvedOrgId, 'commentBank', homeworkType);
};

// Resolves the real, org-configured list of homework type codes - empty
// until the head marker adds some via "Add Homework".
const getOrgHomeworkTypes = async (orgId) => {
  const names = await getHomeworkNames(orgId);
  return sortHomeworkTypes(Object.keys(names));
};

/**
 * Get comment bank for a specific homework
 */
export const getCommentBank = async (homeworkType, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }

    // Return empty structure if doesn't exist
    return {
      sectionA: [],
      sectionB: [],
      sectionC: [],
      final: [],
      suggestions: []
    };
  } catch (error) {
    console.error('Get comment bank error:', error);
    throw error;
  }
};

/**
 * Get all comment banks (for head marker overview)
 */
export const getAllCommentBanks = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const homeworkTypes = await getOrgHomeworkTypes(resolvedOrgId);
    const results = await Promise.all(homeworkTypes.map(type => getCommentBank(type, resolvedOrgId)));
    return Object.fromEntries(homeworkTypes.map((type, i) => [type, results[i]]));
  } catch (error) {
    console.error('Get all comment banks error:', error);
    throw error;
  }
};

/**
 * Add a comment to the bank (head marker)
 */
export const addComment = async (homeworkType, section, commentText, userId, userName, header = null, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const comment = {
  id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  text: commentText,
  header: header || null,
  createdBy: userId,
  createdByName: userName,
  createdAt: new Date().toISOString(),
  approved: true
};

    // Get current data
    const docSnap = await getDoc(docRef);
    const currentData = docSnap.exists() ? docSnap.data() : {
      sectionA: [],
      sectionB: [],
      sectionC: [],
      final: [],
      suggestions: []
    };

    // Add comment to appropriate section
    // Handle both old plain-array format and new { headers, comments } object format
    const sectionKey = section === 'final' ? 'final' : `section${section}`;
    const existing = currentData[sectionKey];
    if (existing && !Array.isArray(existing) && typeof existing === 'object') {
      // New format: { headers: [], comments: [] }
      currentData[sectionKey] = {
        ...existing,
        comments: [...(existing.comments || []), comment]
      };
    } else {
      // Old format: plain array
      currentData[sectionKey] = [...(existing || []), comment];
    }

    await setDoc(docRef, currentData);

    console.log('Comment added:', homeworkType, section);
    return comment;
  } catch (error) {
    console.error('Add comment error:', error);
    throw error;
  }
};

/**
 * Update a comment (head marker)
 */
export const updateComment = async (homeworkType, section, commentId, newText, header = null, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Comment bank not found');
    }

    const data = docSnap.data();
    const sectionKey = section === 'final' ? 'final' : `section${section}`;

    // Find and update the comment — handle both plain-array and { headers, comments } formats
    const existing = data[sectionKey];
    const isNewFormat = existing && !Array.isArray(existing) && typeof existing === 'object';
    const comments = isNewFormat ? (existing.comments || []) : (existing || []);
    const updatedComments = comments.map(comment =>
      comment.id === commentId
        ? { ...comment, text: newText, header: header || null, updatedAt: new Date().toISOString() }
        : comment
    );
    data[sectionKey] = isNewFormat ? { ...existing, comments: updatedComments } : updatedComments;
    await setDoc(docRef, data);

    console.log('Comment updated:', commentId);
  } catch (error) {
    console.error('Update comment error:', error);
    throw error;
  }
};

/**
 * Delete a comment (head marker)
 */
export const deleteComment = async (homeworkType, section, commentId, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Comment bank not found');
    }

    const data = docSnap.data();
    const sectionKey = section === 'final' ? 'final' : `section${section}`;

    // Filter out the comment — handle both plain-array and { headers, comments } formats
    const existing = data[sectionKey];
    const isNewFormat = existing && !Array.isArray(existing) && typeof existing === 'object';
    const comments = isNewFormat ? (existing.comments || []) : (existing || []);
    const filteredComments = comments.filter(comment => comment.id !== commentId);
    data[sectionKey] = isNewFormat ? { ...existing, comments: filteredComments } : filteredComments;

    await setDoc(docRef, data);

    console.log('Comment deleted:', commentId);
  } catch (error) {
    console.error('Delete comment error:', error);
    throw error;
  }
};

/**
 * Suggest a comment (marker)
 */
export const suggestComment = async (homeworkType, section, commentText, userId, userName, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const suggestion = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      section,
      suggestedBy: userId,
      suggestedByName: userName,
      suggestedAt: new Date().toISOString(),
      approved: false
    };

    // Get current data
    const docSnap = await getDoc(docRef);
    const currentData = docSnap.exists() ? docSnap.data() : {
      sectionA: [],
      sectionB: [],
      sectionC: [],
      final: [],
      suggestions: []
    };

    // Add to suggestions
    currentData.suggestions = [...(currentData.suggestions || []), suggestion];

    await setDoc(docRef, currentData);

    console.log('Comment suggested:', homeworkType, section);
    return suggestion;
  } catch (error) {
    console.error('Suggest comment error:', error);
    throw error;
  }
};

/**
 * Approve a suggestion (head marker)
 */
export const approveSuggestion = async (homeworkType, suggestionId, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Comment bank not found');
    }

    const data = docSnap.data();
    const suggestions = data.suggestions || [];

    // Find the suggestion
    const suggestion = suggestions.find(s => s.id === suggestionId);
    if (!suggestion) {
      throw new Error('Suggestion not found');
    }

    const approvedComment = {
      ...suggestion,
      approved: true,
      approvedAt: new Date().toISOString()
    };

    if (suggestion.section === 'finalQuestion') {
      // Route to finalQuestions[questionId]
      const qId = suggestion.questionId;
      const existingFQ = data.finalQuestions || {};
      data.finalQuestions = {
        ...existingFQ,
        [qId]: [...(existingFQ[qId] || []), approvedComment]
      };
    } else {
      // Move to appropriate section (handle both legacy plain array and new { headers, comments } format)
      const sectionKey = suggestion.section === 'final' ? 'final' : `section${suggestion.section}`;
      const existing = data[sectionKey];
      if (Array.isArray(existing) || !existing) {
        data[sectionKey] = [...(existing || []), approvedComment];
      } else {
        data[sectionKey] = { ...existing, comments: [...(existing.comments || []), approvedComment] };
      }
    }
    data.suggestions = suggestions.filter(s => s.id !== suggestionId);

    await setDoc(docRef, data);

    console.log('Suggestion approved:', suggestionId);
  } catch (error) {
    console.error('Approve suggestion error:', error);
    throw error;
  }
};

/**
 * Reject a suggestion (head marker)
 */
export const rejectSuggestion = async (homeworkType, suggestionId, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Comment bank not found');
    }

    const data = docSnap.data();
    data.suggestions = (data.suggestions || []).filter(s => s.id !== suggestionId);

    await setDoc(docRef, data);

    console.log('Suggestion rejected:', suggestionId);
  } catch (error) {
    console.error('Reject suggestion error:', error);
    throw error;
  }
};

/**
 * Get pending suggestions count
 */
export const getPendingSuggestionsCount = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const homeworkTypes = await getOrgHomeworkTypes(resolvedOrgId);
    const banks = await Promise.all(homeworkTypes.map(type => getCommentBank(type, resolvedOrgId)));
    return banks.reduce((total, bank) => total + (bank.suggestions || []).length, 0);
  } catch (error) {
    console.error('Get pending suggestions count error:', error);
    return 0;
  }
};
export const suggestFinalQuestionComment = async (homeworkType, questionId, questionLabel, commentText, userId, userName, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const suggestion = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      section: 'finalQuestion',
      questionId,
      questionLabel,
      suggestedBy: userId,
      suggestedByName: userName,
      suggestedAt: new Date().toISOString(),
      approved: false
    };
    const docSnap = await getDoc(docRef);
    const currentData = docSnap.exists() ? docSnap.data() : {};
    currentData.suggestions = [...(currentData.suggestions || []), suggestion];
    await setDoc(docRef, currentData);
    return suggestion;
  } catch (error) {
    console.error('Suggest final question comment error:', error);
    throw error;
  }
};

export const addFinalQuestionComment = async (homeworkType, questionId, commentText, userId, userName, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);
    const data = docSnap.exists() ? docSnap.data() : {};
    const comment = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      createdBy: userId,
      createdByName: userName,
      createdAt: new Date().toISOString(),
    };
    const existing = data.finalQuestions || {};
    data.finalQuestions = {
      ...existing,
      [questionId]: [...(existing[questionId] || []), comment]
    };
    await setDoc(docRef, data);
    return comment;
  } catch (error) {
    console.error('Add final question comment error:', error);
    throw error;
  }
};

export const deleteFinalQuestionComment = async (homeworkType, questionId, commentId, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;
    const data = docSnap.data();
    const existing = data.finalQuestions || {};
    data.finalQuestions = {
      ...existing,
      [questionId]: (existing[questionId] || []).filter(c => c.id !== commentId)
    };
    await setDoc(docRef, data);
  } catch (error) {
    console.error('Delete final question comment error:', error);
    throw error;
  }
};

export const updateFinalQuestionComment = async (homeworkType, questionId, commentId, newText, orgId) => {
  try {
    const docRef = await getCommentBankRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;
    const data = docSnap.data();
    const existing = data.finalQuestions || {};
    data.finalQuestions = {
      ...existing,
      [questionId]: (existing[questionId] || []).map(c =>
        c.id === commentId ? { ...c, text: newText, updatedAt: new Date().toISOString() } : c
      )
    };
    await setDoc(docRef, data);
  } catch (error) {
    console.error('Update final question comment error:', error);
    throw error;
  }
};

export const updateSectionData = async (homeworkType, sectionKey, sectionData, orgId) => {
  const docRef = await getCommentBankRef(homeworkType, orgId);
  await setDoc(docRef, { [sectionKey]: sectionData }, { merge: true });
};
